/**
 * useMediaQuery - Responsive breakpoint detection hook
 * 
 * Provides consistent responsive detection across the app.
 * Uses CSS media queries for accurate breakpoint detection.
 * 
 * Breakpoints (Tailwind defaults):
 * - sm: 640px
 * - md: 768px  
 * - lg: 1024px
 * - xl: 1280px
 * - 2xl: 1536px
 */

import { useState, useEffect, useCallback } from 'react';

// Standard breakpoints matching Tailwind
const BREAKPOINTS = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  '2xl': 1536,
};

/**
 * Generic media query hook
 * @param {string} query - CSS media query string
 * @returns {boolean} - Whether the query matches
 */
export function useMediaQuery(query) {
  const [matches, setMatches] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.matchMedia(query).matches;
    }
    return false;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const mediaQuery = window.matchMedia(query);
    setMatches(mediaQuery.matches);

    const handler = (event) => setMatches(event.matches);
    
    // Modern browsers
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handler);
      return () => mediaQuery.removeEventListener('change', handler);
    }
    // Legacy browsers
    mediaQuery.addListener(handler);
    return () => mediaQuery.removeListener(handler);
  }, [query]);

  return matches;
}

/**
 * Check if screen is mobile (< 768px)
 * @returns {boolean}
 */
export function useIsMobile() {
  return useMediaQuery(`(max-width: ${BREAKPOINTS.md - 1}px)`);
}

/**
 * Check if screen is tablet (768px - 1023px)
 * @returns {boolean}
 */
export function useIsTablet() {
  return useMediaQuery(`(min-width: ${BREAKPOINTS.md}px) and (max-width: ${BREAKPOINTS.lg - 1}px)`);
}

/**
 * Check if screen is desktop (>= 1024px)
 * @returns {boolean}
 */
export function useIsDesktop() {
  return useMediaQuery(`(min-width: ${BREAKPOINTS.lg}px)`);
}

/**
 * Get current breakpoint name
 * @returns {'mobile' | 'sm' | 'md' | 'lg' | 'xl' | '2xl'}
 */
export function useBreakpoint() {
  const [breakpoint, setBreakpoint] = useState('mobile');

  const updateBreakpoint = useCallback(() => {
    if (typeof window === 'undefined') return;
    
    const width = window.innerWidth;
    if (width >= BREAKPOINTS['2xl']) {
      setBreakpoint('2xl');
    } else if (width >= BREAKPOINTS.xl) {
      setBreakpoint('xl');
    } else if (width >= BREAKPOINTS.lg) {
      setBreakpoint('lg');
    } else if (width >= BREAKPOINTS.md) {
      setBreakpoint('md');
    } else if (width >= BREAKPOINTS.sm) {
      setBreakpoint('sm');
    } else {
      setBreakpoint('mobile');
    }
  }, []);

  useEffect(() => {
    updateBreakpoint();
    window.addEventListener('resize', updateBreakpoint);
    return () => window.removeEventListener('resize', updateBreakpoint);
  }, [updateBreakpoint]);

  return breakpoint;
}

/**
 * Check if screen width is at or above a breakpoint
 * @param {'sm' | 'md' | 'lg' | 'xl' | '2xl'} breakpoint
 * @returns {boolean}
 */
export function useMinWidth(breakpoint) {
  const width = BREAKPOINTS[breakpoint] || 0;
  return useMediaQuery(`(min-width: ${width}px)`);
}

/**
 * Check if screen width is below a breakpoint
 * @param {'sm' | 'md' | 'lg' | 'xl' | '2xl'} breakpoint
 * @returns {boolean}
 */
export function useMaxWidth(breakpoint) {
  const width = BREAKPOINTS[breakpoint] || 0;
  return useMediaQuery(`(max-width: ${width - 1}px)`);
}

export default useMediaQuery;
